import React, { useEffect, useRef } from 'react';
import { Form, Formik, FormikValues } from 'formik';
import * as Yup from 'yup';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';

import FormikGrid from '../../../common/formik/FormikGrid';
import FormikButton from '../../../common/formik/FormikButton';
import FormikTextField from '../../../common/formik/FormikTextField';
import { Product } from '../Product.interface';
import { useUpdateProduct } from '../Product.hooks';

export interface ProductRenameDialogProps {
  open: boolean;
  product: Product;
  onCancel?: () => void;
  onChange?: (product: Partial<Product>) => void;
  onUpdate?: () => void;
}

const ProductRenameDialog: React.FC<ProductRenameDialogProps> = (
  props: ProductRenameDialogProps
) => {
  const { open, product, onCancel, onChange, onUpdate } = props;
  const formikRef = useRef<FormikValues>();
  const {
    updatedProduct,
    isUpdating,
    updateError,
    updateProduct
  } = useUpdateProduct(product.id);

  useEffect(() => {
    formikRef.current?.setSubmitting?.(isUpdating);
  }, [isUpdating]);

  useEffect(() => {
    if (updatedProduct) {
      onChange?.(updatedProduct);
      onUpdate?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedProduct]);

  return (
    <Dialog maxWidth='xs' open={open} onClose={onCancel}>
      {isUpdating && <LinearProgress />}
      <Formik
        innerRef={(formik: any) => (formikRef.current = formik)}
        initialValues={{ name: product.name } as Partial<Product>}
        validationSchema={Yup.object({
          name: Yup.string().required('Product name is required.')
        })}
        onSubmit={(dto: Partial<Product>) => {
          updateProduct(dto);
          onChange?.(dto);
        }}
      >
        <Form autoComplete='off'>
          <DialogTitle>Rename Product</DialogTitle>
          <DialogContent>
            <FormikGrid>
              <FormikTextField
                autoFocus
                type='text'
                name='name'
                label='Product Name'
                disabled={isUpdating}
              />
            </FormikGrid>
            {updateError && (
              <Alert severity='error'>{`Error occurred. ${updateError.message}`}</Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={onCancel}>Cancel</Button>
            <FormikButton>Rename</FormikButton>
          </DialogActions>
        </Form>
      </Formik>
    </Dialog>
  );
};

export default ProductRenameDialog;
